/**
 * ============================================================================
 * Header search (Fashion Nova style)
 * ============================================================================
 * Turns the header's search field into a live panel: predictive results, recent
 * searches, keyboard navigation. Drives snippets/header-search.liquid.
 *
 * - Desktop: focusing the input opens a dropdown panel under it; an outside click
 *   or Escape closes it.
 * - Mobile: the magnifier button opens a full-screen overlay (input + panel); the
 *   close button, backdrop or Escape closes it.
 *
 * Degrades without this file: the field is a real GET form to /search, so Enter
 * still works. Results come back as rendered markup (Section Rendering API), so
 * prices are already formatted in the visitor's currency.
 */
(() => {
  const root = document.querySelector('[data-search-root]');
  if (!root) return;

  const form = root.querySelector('.hsearch__form');
  const input = root.querySelector('[data-search-input]');
  const panel = root.querySelector('[data-search-panel]');
  const slot = root.querySelector('[data-search-slot]');
  const fallback = root.querySelector('[data-search-default]');
  const waiting = root.querySelector('[data-search-loading]');
  const clear = root.querySelector('[data-search-clear]');
  const toggle = root.querySelector('[data-search-toggle]');
  const closeBtn = root.querySelector('[data-search-close]');
  const recentBox = root.querySelector('[data-search-recent]');
  const recentList = root.querySelector('[data-search-recent-list]');
  const deptBtns = root.querySelectorAll('[data-search-dept]');

  if (!input) return;
  const predictive = !!root.dataset.suggestUrl && !!slot;

  /* --- Department (Women / Men) --- *
   * Women is the default and excludes men-tagged products; Men shows only them.
   * The panel scopes client-side; the submit carries filter.p.tag=Men so the
   * results page returns men-only server-side. */
  const DEPTS = { men: 'Men', kids: 'Kids', pet: 'pet' };
  let dept = DEPTS[root.dataset.dept] ? root.dataset.dept : 'women';
  function syncDeptFilter() {
    if (!form) return;
    var existing = form.querySelector('input[name="filter.p.tag"][data-dept-filter]');
    if (existing) existing.remove();
    if (DEPTS[dept]) {
      var inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = 'filter.p.tag';
      inp.value = DEPTS[dept];
      inp.setAttribute('data-dept-filter', '');
      form.appendChild(inp);
    }
  }
  // Rewrites a results-page link to products only, in the current department.
  function scopeHref(href) {
    let u = (href || '').split('&type=')[0].split('&filter.p.tag=')[0];
    u += '&type=product';
    if (DEPTS[dept]) u += '&filter.p.tag=' + DEPTS[dept];
    return u;
  }
  // Default state: show the image rails and popular chips for the department
  // (the beauty rails on a beauty surface while the Women tab is active).
  function applyDefaults() {
    const scope = dept === 'women' && root.dataset.brand === 'beauty' ? 'beauty' : dept;
    root.querySelectorAll('[data-search-rail]').forEach((el) => { el.hidden = el.dataset.searchRail !== scope; });
    const hot = scope === 'beauty' ? 'women' : scope;
    root.querySelectorAll('[data-search-hot]').forEach((el) => { el.hidden = el.dataset.searchHot !== hot; });
  }
  function applyDept() {
    if (!slot) return;
    slot.querySelectorAll('[data-search-coll-dept]').forEach((chip) => { chip.hidden = chip.dataset.searchCollDept !== dept; });
    slot.querySelectorAll('[data-search-query]').forEach((a) => a.setAttribute('href', scopeHref(a.getAttribute('href'))));
    slot.querySelectorAll('li').forEach((li) => {
      if (!li.querySelector('[data-search-result]')) return; // only product rows carry results
      // Tag first, title as a fallback for pieces missing the department tag.
      const title = (li.querySelector('[data-search-title]')?.dataset.searchTitle || '').toLowerCase();
      const men = li.hasAttribute('data-search-men') || /\bmen'?s\b/.test(title);
      const kids = li.hasAttribute('data-search-kids') || /\b(kids'?|children'?s)\b/.test(title);
      const pet = li.hasAttribute('data-search-pet');
      let hide;
      if (dept === 'men') hide = !men;
      else if (dept === 'kids') hide = !kids;
      else if (dept === 'pet') hide = !pet;
      else hide = men || kids || pet; // women: exclude men, kids and pet
      li.hidden = hide;
    });
    slot.querySelectorAll('.psearch__group').forEach((group) => {
      const rows = group.querySelectorAll('li, [data-search-coll-dept]');
      group.hidden = !!rows.length && Array.from(rows).every((el) => el.hidden);
    });
    // Retarget the "view all" link to the current department (predictive cannot
    // scope men server-side, so this is the reliable path to full men's results).
    const all = slot.querySelector('.psearch__all');
    if (all) {
      all.setAttribute('href', scopeHref(all.getAttribute('href')));
      all.hidden = false;
    }
  }
  let reask = null;   // set by the predictive block: re-run the current term
  function setDept(next) {
    dept = DEPTS[next] ? next : 'women';
    // An explicit department pick leaves the beauty-only scope of a beauty page.
    if (root.dataset.brand && dept !== 'women') root.dataset.brand = '';
    deptBtns.forEach((b) => {
      const on = b.dataset.searchDept === dept;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    syncDeptFilter();
    applyDefaults();
    applyDept();
    if (reask) reask();
  }
  deptBtns.forEach((b) => b.addEventListener('click', () => { setDept(b.dataset.searchDept); if (!isMobile()) input.focus(); }));
  syncDeptFilter();

  const RECENT_KEY = 'recto:recent-searches';
  const MIN = 2;
  const KEEP = 6;

  let timer = null, waitTimer = null, controller = null, active = -1;
  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;

  /* --- Recent (this browser only, never a request) --- */
  function readRecent() {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) { return []; }
  }
  function remember(term) {
    const clean = String(term || '').trim();
    if (clean.length < MIN) return;
    try {
      const list = readRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase());
      list.unshift(clean);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, KEEP)));
    } catch (e) { /* ignore */ }
  }
  function forget() {
    try { window.localStorage.removeItem(RECENT_KEY); } catch (e) { /* ignore */ }
    paintRecent();
  }
  const CLOCK =
    '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
  function forgetOne(term) {
    try {
      const list = readRecent().filter((t) => t !== term);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) { /* ignore */ }
    paintRecent();
  }
  const CROSS =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  // Recent searches as rows (clock, term, remove), like Fashion Nova.
  function paintRecent() {
    if (!recentBox || !recentList) return;
    const list = readRecent();
    recentList.textContent = '';
    if (!list.length) { recentBox.hidden = true; return; }
    list.forEach((term) => {
      const li = document.createElement('li');
      li.className = 'psearch__recent-row';
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'psearch__recent-term';
      go.dataset.searchTerm = term;
      go.innerHTML = CLOCK;
      go.appendChild(document.createTextNode(term));
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'psearch__recent-x';
      x.setAttribute('aria-label', 'Remove ' + term);
      x.innerHTML = CROSS;
      x.addEventListener('click', (e) => { e.stopPropagation(); forgetOne(term); input.focus({ preventScroll: true }); });
      li.append(go, x);
      recentList.appendChild(li);
    });
    recentBox.hidden = false;
  }

  /* --- Open / close --- */
  function open() {
    if (root.classList.contains('is-open')) return;
    root.classList.add('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (predictive) paintRecent();
    if (isMobile()) {
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => { input.focus(); });
    }
  }
  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    input.removeAttribute('aria-activedescendant');
    active = -1;
  }

  if (toggle) toggle.addEventListener('click', () => { open(); if (!isMobile()) input.focus(); });
  if (closeBtn) closeBtn.addEventListener('click', close);
  input.addEventListener('focus', open);

  // Opening visual search closes the text search panel, so the two never stack.
  document.addEventListener('click', (e) => { if (e.target.closest('[data-vs-open]')) close(); });
  // Re-opening the photo panel on the visual results page closes it too.
  document.addEventListener('vs:expand', close);

  // Desktop: an outside click closes the dropdown. (On mobile it is a full overlay.)
  document.addEventListener('click', (e) => {
    if (!root.classList.contains('is-open')) return;
    if (isMobile()) return;
    if (!root.contains(e.target)) close();
  });

  /* --- Asking (predictive) --- */
  if (predictive) {
    const FASHION_RE = /\b(bag|clip|scrunchie|headband|hairband|hat|beanie|scarf|belt|glove|sock|dress|gown|top|blouse|tee|shirt|sweater|cardigan|jean|denim|pant|trouser|legging|skirt|short|jumpsuit|romper|coord|two[- ]?piece|swimsuit|bikini|swim|jewel|necklace|earring|bracelet|anklet|ring|pendant|bodysuit|cami|jacket|coat|blazer|shoe|boot|sandal|heel|sneaker)\b/;
    const BEAUTY_RE = /\b(serum|cleanser|moisturi[sz]|cream|lotion|toner|mask|exfoliat|niacinamide|hyaluronic|retinol|vitamin\s*c|spf|sunscreen|shampoo|conditioner|shaver|epilator|clipper|trimmer|straighten|curler|blow\s*dry|makeup|lipstick|mascara|concealer|foundation|blush|nail|polish|fragrance|perfume|cologne|facial|skincare|balm|scrub|peel|cuticle|wax|deodorant|body\s*(?:wash|oil|butter|scrub))\b/;
    const isBeauty = (title) => { const t = String(title || '').toLowerCase(); if (FASHION_RE.test(t)) return false; return BEAUTY_RE.test(t); };
    function applyBrand() {
      const brand = root.dataset.brand || '';
      if (!brand) return;
      slot.querySelectorAll('[data-search-title]').forEach((row) => {
        const li = row.closest('li') || row;
        const beauty = isBeauty(row.dataset.searchTitle);
        // Only ever hide: applyDept runs first and decides the rest.
        if (brand === 'beauty' ? !beauty : beauty) li.hidden = true;
      });
      slot.querySelectorAll('.psearch__group').forEach((group) => {
        const rows = group.querySelectorAll('li');
        if (rows.length && Array.from(rows).every((li) => li.hidden)) group.hidden = true;
      });
    }

    // Typing swaps the camera for a clear button on mobile (has-term), like Fashion Nova.
    function showClear(hasTerm) { if (clear) clear.hidden = !hasTerm; root.classList.toggle('has-term', hasTerm); }
    reask = () => { const t = input.value.trim(); if (t.length >= MIN) ask(t); };
    showClear(input.value.trim().length > 0);
    function reset() {
      slot.hidden = true; slot.textContent = '';
      if (fallback) fallback.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      active = -1;
    }
    function items() {
      return Array.from(root.querySelectorAll('[data-search-result], [data-search-term]')).filter((el) => el.offsetParent !== null);
    }

    const FALLBACK_KEY = 'recto:suggest-fallback';   // locale endpoint known to 417 this session
    let suggestUrl = root.dataset.suggestUrl;
    try { if (sessionStorage.getItem(FALLBACK_KEY) === suggestUrl) suggestUrl = '/search/suggest'; } catch (e) { /* ignore */ }
    // "/ar" on the Arabic storefront, "" on the default language.
    const LOCALE_PREFIX = (root.dataset.searchUrl || '/search').replace(/\/search$/, '');

    async function ask(term) {
      if (controller) controller.abort();
      controller = new AbortController();
      clearTimeout(waitTimer);
      waitTimer = setTimeout(() => { if (waiting) waiting.hidden = false; }, 180);

      // Predictive search can't filter by tag, and its top hits are women's, so the
      // Men / Kids tabs weight the query with the department word ("men's shirt").
      const q = dept === 'men' ? "men's " + term : dept === 'kids' ? 'kids ' + term : term;
      const url = suggestUrl + '?q=' + encodeURIComponent(q) +
        '&section_id=predictive-search' +
        '&resources[type]=' + encodeURIComponent(root.dataset.types || 'product') +
        '&resources[limit]=' + encodeURIComponent(root.dataset.limit || 6) +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=' + encodeURIComponent(root.dataset.fields || 'title,product_type,variants.title,vendor');

      try {
        const response = await fetch(url, { signal: controller.signal });
        // Shopify's predictive search rejects some storefront languages (Arabic
        // answers 417 "Unsupported buyer locale"). Fall back to the default-language
        // endpoint once, and keep the shopper in their language by re-prefixing links.
        if (response.status === 417 && suggestUrl !== '/search/suggest') {
          try { sessionStorage.setItem(FALLBACK_KEY, suggestUrl); } catch (e) { /* ignore */ }
          suggestUrl = '/search/suggest';
          return ask(term);
        }
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        // Drop a reply the shopper has moved past (cleared, or typed something else).
        if (input.value.trim() !== term) return;
        const fresh = document.createRange().createContextualFragment(html).querySelector('[data-predictive-results]');
        if (!fresh) return;
        clearTimeout(waitTimer);
        if (waiting) waiting.hidden = true;
        if (suggestUrl !== root.dataset.suggestUrl && LOCALE_PREFIX) {
          fresh.querySelectorAll('a[href^="/"]:not([href^="//"])').forEach((a) => {
            const h = a.getAttribute('href');
            if (!h.startsWith(LOCALE_PREFIX + '/')) a.setAttribute('href', LOCALE_PREFIX + h);
          });
        }
        slot.textContent = '';
        slot.appendChild(fresh);
        slot.hidden = false;
        if (fallback) fallback.hidden = true;
        applyDept();
        applyBrand();
        input.setAttribute('aria-expanded', fresh.dataset.count !== '0' ? 'true' : 'false');
        active = -1;
      } catch (failed) {
        if (failed.name === 'AbortError') return;
        clearTimeout(waitTimer);
        if (waiting) waiting.hidden = true;
        reset();
      }
    }

    input.addEventListener('input', () => {
      const term = input.value.trim();
      showClear(term.length > 0);
      clearTimeout(timer);
      if (term.length < MIN) {
        if (controller) controller.abort();
        clearTimeout(waitTimer);
        if (waiting) waiting.hidden = true;
        reset();
        return;
      }
      timer = setTimeout(() => ask(term), 240);
    });

    if (clear) clear.addEventListener('click', () => {
      input.value = ''; showClear(false);
      clearTimeout(timer); clearTimeout(waitTimer);
      if (controller) controller.abort();
      if (waiting) waiting.hidden = true;
      reset(); input.focus();
    });

    // A chip is a term, not a destination: fill the field and ask.
    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-search-term]');
      if (!chip) return;
      e.preventDefault();
      input.value = chip.dataset.searchTerm;
      showClear(true);
      input.focus();
      ask(input.value);
    });
    if (root.querySelector('[data-search-forget]')) {
      root.addEventListener('click', (e) => { if (e.target.closest('[data-search-forget]')) forget(); });
    }
    root.addEventListener('click', (e) => { if (e.target.closest('[data-search-result]')) remember(input.value); });

    /* --- Keyboard --- */
    function highlight(next) {
      const list = items();
      if (!list.length) return;
      list.forEach((el) => el.classList.remove('is-active'));
      active = (next + list.length) % list.length;
      const el = list[active];
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
      if (el.id === '') el.id = 'search-result-' + active;
      input.setAttribute('aria-activedescendant', el.id);
    }
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (input.value) { e.preventDefault(); if (clear) clear.click(); else { input.value = ''; showClear(false); reset(); } return; }
        close();
        return;
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); return; }
      if (e.key === 'Enter' && active > -1) {
        const el = items()[active];
        if (el) { e.preventDefault(); remember(input.value); el.click(); }
      }
    });
  } else {
    // No predictive: Escape still closes the (mobile) overlay.
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  // Keep the term when it is submitted (a half-typed word is not a search).
  if (form) form.addEventListener('submit', () => remember(input.value));
})();
